/**
 * Step 3's plans — the granny flat on the aged care spine (rebuild, 21 Oct 2026; the agreed mock-up's
 * j1 plans screen, as the village's RvPlansView).
 *
 *  - Not entering the arrangement as a strip of the four numbers from step 2; every card is read against it.
 *  - One verdict line, computed: whether any way of setting it up leaves more in their estate; that part of
 *    the gap is what they give the person they live with (still theirs to that person, not lost); and what
 *    leaves the household altogether — the lost Age Pension, naming the plans that keep it.
 *  - "What matters most to you?" — the four goals; choosing one never re-runs the engine.
 *  - The cards side by side, aligned row by row: the plan in words, what is left IN THEIR ESTATE (owner,
 *    19 Oct 2026: what goes to the person they live with is not counted as lost), two bars — less in the
 *    estate, and given to that person — then Age Pension / Left over each year / Counted as a gift (review
 *    decision 4: the gift is the rule a granny flat turns on), an Important or Worth knowing line, and the
 *    three buttons.
 *  - The ways on: compare, every way tested, the family discussion guide, things that change the answer,
 *    build a plan yourself.
 *
 * Presentation only. Every figure is the engine row's own.
 */
import React from 'react';
import EnginePlanCard, { type PlanCardNumber } from '../agedCare/EnginePlanCard.tsx';
import EngineGoalControl, { type GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { readMeansTestPosition, type MeansTestBreakdownRow } from '../../components/insights/MeansTestPosition.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { runwayCardLine } from '../agedCare/moneyStory.tsx';
import { goalLeaderFor, NEAR_BEST_LABEL, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { allPlansOpenLine, secondPlanLine, secondPlanChip, leadChip, leadLine, SECOND_PLAN_CHIP } from '../../strategy/planChoiceRule.ts';
import { CASHFLOW_COVERED_LABEL, coveredIsBestEstate } from '../../strategy/cashflowCoveredGoal.ts';
import { EXTRA_ALLOWABLE_AMOUNT, GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { IncomeOnlyBeforeChoose, incomeOnlyHeld } from '../../components/strategy/IncomeOnlyTick.tsx';
import { gfPlanWords, gfMoveOf, gfGivenOf, gfPensionReason, gfPlanGerund, gfGiftCounted } from './gfPlanWords.ts';
import { gfGiveOf } from './GfStep1.tsx';
import { gfBuildNeedsCost } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import { gfHostPhrase, gfHostKindOf, gfEstateWords, gfChildrenCount, gfPensionOnlyLeaderLine } from './gfPresentation.ts';
import { gfGuaranteedIncomeOf, gfComparePlanFor, GF_COMPARE_NOTE } from './gfGuaranteedIncome.ts';
import type { GfStrategyView } from './grannyFlatTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FinancialPosition } from '../agedCare/FinancialImpactPanel.tsx';
import OtherGoalLeaders from '../../components/strategy/OtherGoalLeaders.tsx';
import { RENTED_HOME_CGT_CARD } from '../../strategy/strategyRisks.ts';
import SetAsideReveal from '../../components/strategy/SetAsideReveal.tsx';
import { benchmarkWords, householdRents } from '../../utils/benchmarkWords.ts';
import { ChosenAdviceLine } from '../../components/strategy/ChosenAdviceLine.tsx';

export interface GfSetAside { strategy: GfStrategyView; labels: string[]; warning: string }

export interface GfPlansViewProps {
    factFind: DraftFactFind;
    strategies: GfStrategyView[];
    stayPut: GfStrategyView | null;
    recommended: RecommendedStrategyEntry[];
    setAside: GfSetAside[];
    horizonYears: number;
    /** What the household owns today (step 2's third number). */
    ownToday: number;
    focus: GoalFocusId;
    onFocus: (f: GoalFocusId) => void;
    chosenId: string | null;
    onChoose: (row: GfStrategyView) => void;
    onExplain: (row: GfStrategyView) => void;
    onRisks: (row: GfStrategyView) => void;
    renderDetail: (row: GfStrategyView) => React.ReactNode;
    onAllWays: () => void;
    onChanges: () => void;
    onBuildYourOwn: (row: GfStrategyView | null) => void;
    onAdvice: () => void;
    onFamilyGuide: () => void;
    /** Above everything: the chosen banner. */
    notices?: React.ReactNode;
    /** Records (or withdraws) the client's income-only confirmation for an amount (incomeOnlySetter). */
    onIncomeOnly?: (confirmed: boolean, amount: number) => void;
}

const isMover = (s: GfStrategyView) => s.id !== 'gf-stay-put';
const legacyOf = (s: GfStrategyView | null | undefined) => Number(s?.snapshot?.legacyValue) || 0;
const pensionOf = (s: GfStrategyView | null | undefined) => Number(s?.snapshot?.pension) || 0;
const cashOf = (s: GfStrategyView | null | undefined) => Number(s?.snapshot?.netCashflow) || 0;
const money = (n: number) => formatCurrency(Math.round(n));
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/**
 * The verdict line, computed (review: "the verdict line says what costs the family altogether — the lost
 * Age Pension — and what only moves within it"). Said as awareness, never alarm (owner, 19 Oct 2026).
 */
export function gfVerdictWords(p: { factFind: DraftFactFind; strategies: GfStrategyView[]; stayPut: GfStrategyView | null; horizonYears: number }): { cost: boolean; text: string } | null {
    const v = householdVoice(p.factFind);
    const movers = p.strategies.filter(isMover);
    const base = p.stayPut;
    if (!base || movers.length === 0) return null;
    const best = movers.reduce((top, s) => (legacyOf(s) > legacyOf(top) ? s : top), movers[0]!);
    const estate = gfEstateWords(p.factFind);
    const host = gfHostPhrase(p.factFind);
    const hostKind = gfHostKindOf(p.factFind);
    const yrs = `${p.horizonYears} ${p.horizonYears === 1 ? 'year' : 'years'}`;
    const basePension = pensionOf(base);
    const keepers = movers.filter((s) => pensionOf(s) >= 1);
    const keeperWords = keepers.length > 0 && keepers.length <= 3 ? joinAnd([...new Set(keepers.map((s) => gfPlanGerund(s, p.factFind)))]) : '';
    const pensionWords = basePension >= 1
        ? keepers.length === 0
            ? ` The cost that leaves ${v.them} altogether is the lost Age Pension, ${money(basePension)} a year in every plan.`
            : keepers.length < movers.length
                ? ` The cost that leaves ${v.them} altogether is the lost Age Pension, ${money(basePension)} a year in every plan${keeperWords ? ` except ${keeperWords}` : ' but the few that keep it'}.`
                : ''
        : '';
    if (legacyOf(best) < legacyOf(base) - 1) {
        const within = hostKind === 'child' || hostKind === 'family'
            ? `it is still in the family, but it is ${host}’s, not the estate’s`
            : `it is not lost, but it is ${hostKind ? `${host}’s` : 'theirs'}, not the estate’s`;
        return {
            cost: true,
            text: `Every way of setting this up leaves less in ${estate} than staying where ${v.they} are. We tested ${movers.length}. Part of that is what ${v.they} give ${host} — ${within}.${pensionWords}`,
        };
    }
    return {
        cost: false,
        text: `The best way of setting this up leaves ${money(legacyOf(best) - legacyOf(base))} more in ${estate} than staying where ${v.they} are, after about ${yrs}. We tested ${movers.length}.${pensionWords}`,
    };
}

/** The line for a plan whose way of paying differs from step 1's, or null (24 Oct 2026). */
export function gfOtherFormLine(give: string, m: { justMovingIn?: boolean; construction?: number; propertyTransferred?: number; amountPaid?: number; entryContribution?: number }): string | null {
    if (give === 'unsure' || !(m.entryContribution || m.justMovingIn)) return null;
    const form = m.justMovingIn ? 'nothing' : (m.propertyTransferred || 0) > 0 ? 'home' : (m.construction || 0) > 0 ? 'build' : 'cash';
    if (form === give) return null;
    const said = give === 'build' ? 'the build you described' : give === 'home' ? 'signing the home over, as you described' : 'the cash you described';
    const does = form === 'nothing' ? 'Pays nothing for the right' : form === 'home' ? 'Signs the home over' : form === 'build' ? 'Pays for a build' : 'Pays cash';
    return `${does}, not ${said}, for comparison.`;
}

const GfPlansView = ({
    factFind, strategies, stayPut, recommended, setAside, horizonYears, ownToday, focus, onFocus,
    chosenId, onChoose, onExplain, onRisks, renderDetail, onAllWays, onChanges, onBuildYourOwn, onAdvice, onFamilyGuide, notices, onIncomeOnly,
}: GfPlansViewProps) => {
    const v = householdVoice(factFind);
    const [openDetail, setOpenDetail] = React.useState<string | null>(null);
    const [why, setWhy] = React.useState<{ row: GfStrategyView; page: 'pension' | 'cashflow' | 'gift' } | null>(null);
    const [comparing, setComparing] = React.useState(false);
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    const host = gfHostPhrase(factFind);
    const hostKind = gfHostKindOf(factFind);
    const estate = gfEstateWords(factFind);

    const movers = strategies.filter(isMover);
    const byName = (name?: string) => strategies.find((s) => s.name === name) ?? null;
    const fullRow = (r: RecommendedStrategyEntry | null): GfStrategyView | null => (r?.strategy?.name ? (byName(r.strategy.name) || (r.strategy as GfStrategyView)) : null);
    const coveredSame = coveredIsBestEstate(recommended);
    const effective: GoalFocusId = focus === 'covered' && coveredSame ? 'legacy' : focus;
    const base = stayPut?.snapshot ? stayPut : null;
    const noPensionAnyWay = movers.length > 0 && movers.every((s) => pensionOf(s) < 1);
    const keepers = movers.filter((s) => pensionOf(s) >= 1);

    const leader = fullRow(goalLeaderFor(recommended, effective));
    const secondEntry = effective === 'legacy' ? recommended.find((r) => r.label === NEAR_BEST_LABEL && r.second) ?? null : null;
    const secondRow = secondEntry ? fullRow(secondEntry) : null;
    const estateEntry = recommended.find((r) => r.label === 'Optimal Legacy') ?? null;
    const pensionLeader = fullRow(goalLeaderFor(recommended, 'pension'));
    /*
     * ONE CARD PER GOAL, A SECOND ONLY WITH A PURPOSE (owner, 22 Oct 2026: "each section has a specific purpose … only
     * show the second card when there is a purpose to it"). The second card is the plan choice rule's near-tie beside
     * the estate leader (nearly the same estate, less locked away or a better cashflow — planChoiceRule.ts), as aged
     * care and support at home do. Another goal's leader is pointed to in one line under the cards (OtherGoalLeaders).
     */
    const showPensionNote = effective === 'pension' && noPensionAnyWay;
    const shown = showPensionNote || !leader ? [] : [leader, ...(secondRow && secondRow.name !== leader.name ? [secondRow] : [])];
    const others = (['legacy', 'cashflow', 'pension'] as const)
        .filter((g) => g !== effective)
        .map((g) => ({ g, row: g === 'pension' ? pensionLeader : fullRow(goalLeaderFor(recommended, g)) }))
        .filter((x) => !!x.row && !shown.some((s) => s.name === x.row!.name))
        .map((x) => ({ goal: x.g, title: gfPlanWords(x.row!, factFind).title, legacy: legacyOf(x.row), cashflow: cashOf(x.row), pension: pensionOf(x.row) }));
    // “Income covers the costs” only on a plan that does: when none does, that goal falls back to the estate leader,
    // and the label on a plan that is short each year would contradict its own figures (review, 22 Oct 2026).
    const labelsFor = (row: GfStrategyView): string[] => recommended.filter((r) => r.strategy?.name === row.name).map((r) => String(r.label))
        .filter((l) => l !== CASHFLOW_COVERED_LABEL || (Number(row.snapshot?.netCashflow) || 0) >= 0);
    const chipFor = (l: string): string | undefined => {
        if (l === 'Optimal Legacy') return leadChip(estateEntry?.lead).replace(/^Leaves the most$/, 'Leaves the most in the estate');
        if (l === NEAR_BEST_LABEL && secondEntry?.second) return secondPlanChip(secondEntry.second);
        if (l === 'Optimal Age Pension') return keepers.length > 0 && keepers.length < movers.length ? 'Keeps the Age Pension' : 'Highest Age Pension';
        if (l === CASHFLOW_COVERED_LABEL) return 'Income covers the costs';
        if (l === 'Optimal Cashflow') return 'Most left over each year';
        return undefined;
    };

    const verdict = gfVerdictWords({ factFind, strategies, stayPut, horizonYears });
    const maxGain = Math.max(1, ...shown.map((s) => Math.abs(legacyOf(s) - legacyOf(base))));
    const maxGiven = Math.max(1, ...shown.map((s) => gfGivenOf(gfMoveOf(s, factFind))));
    const twoOrMoreChildren = gfChildrenCount(factFind) >= 2 && (hostKind === 'child' || hostKind == null);

    const giftCaption = (row: GfStrategyView): string => {
        const m = gfMoveOf(row, factFind);
        if (m.justMovingIn || m.entryContribution === 0) return 'nothing paid for the right';
        if (gfGiftCounted(m.deprivation) > 0) return 'counted for five years';
        if (m.deprivation > 0) return 'within what can be given freely';
        if (m.amountPaid === 0 && (m.propertyTransferred > 0 || m.construction > 0)) return 'taken at face value';
        return 'within Centrelink’s limit';
    };

    const card = (row: GfStrategyView) => {
        const key = String(row.id ?? row.name);
        const words = gfPlanWords(row, factFind);
        const m = gfMoveOf(row, factFind);
        const given = gfGivenOf(m);
        const chip = labelsFor(row).map(chipFor).filter(Boolean).map((c, i) => (i === 0 ? c! : lower(c!))).join(' · ') || null;
        const delta = base ? legacyOf(row) - legacyOf(base) : null;
        const pDelta = base ? pensionOf(row) - pensionOf(base) : 0;
        const cDelta = base ? cashOf(row) - cashOf(base) : 0;
        const reason = gfPensionReason(row, factFind);
        const numbers: PlanCardNumber[] = [
            {
                label: 'Age Pension', value: pensionOf(row), unit: 'a year',
                vs: base && Math.abs(pDelta) >= 1
                    ? <><span className={`font-bold ${pDelta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(pDelta)} a year</span>{reason && pDelta < 0 ? <> — {reason}</> : <> vs staying put</>}</>
                    : 'unchanged',
                tone: pDelta > 0 ? 'good' : 'neutral',
                onWhy: () => setWhy({ row, page: 'pension' }),
            },
            {
                label: cashOf(row) < 0 ? 'Each year, short by' : 'Left over each year', value: Math.abs(cashOf(row)),
                vs: base
                    ? Math.abs(cDelta) < 1 ? 'unchanged' : <>in the first year; was {cashOf(base) < 0 ? `short ${money(-cashOf(base))}` : money(cashOf(base))}</>
                    : '',
                tone: cashOf(row) >= 0 ? 'good' : 'neutral',
                onWhy: () => setWhy({ row, page: 'cashflow' }),
            },
            {
                // Counted after the $10,000 a year that can be given freely, as the calc counts it (24 Oct 2026).
                label: 'Counted as a gift', value: gfGiftCounted(m.deprivation),
                vs: giftCaption(row),
                tone: gfGiftCounted(m.deprivation) > 0 ? 'bad' : 'neutral',
                onWhy: given > 0 ? () => setWhy({ row, page: 'gift' }) : undefined,
            },
        ];
        /*
         * "When the money runs out" ON EVERY CARD, every account in the order it is drawn (owner, 22 Oct 2026: as every
         * module's card now says it — runwayCardLine). The older headline stays only as the fallback when the accounts
         * cannot be read.
         */
        const runway = row.projections && row.projections.length ? planRunwayCopy(row.projections as never, horizonYears) : null;
        const runText = row.projections && row.projections.length ? runwayCardLine({ rows: row.projections as never, factFind, horizonYears, spentOn: 'the granny flat' }) : null;
        const runline = runText
            ? <span data-testid={`gf-card-runway-${row.id}`}><strong className="text-slate-900">When the money runs out:</strong> {runText}</span>
            : runway && runway.depleted ? (
                <><strong className="text-slate-900">When the money runs out:</strong> {lower(runway.headline)}.{runway.flag ? ` ${runway.flag}` : ''}</>
            ) : null;
        const lines: React.ReactNode[] = [];
        const baseDelta = base ? legacyOf(row) - legacyOf(base) : 0;
        if (m.home === 'transferred') {
            lines.push(<><strong className="text-rose-700">Important:</strong> the home becomes {host}’s.{baseDelta < -1 ? <> {v.Possessive} estate falls by <strong>{money(-baseDelta)}</strong></> : null}{twoOrMoreChildren ? <>{baseDelta < -1 ? ' — and the' : ' The'} {gfChildrenCount(factFind) === 2 ? 'other child’s' : 'other children’s'} share of the family&apos;s wealth falls with it unless the Will evens it out</> : null}{baseDelta < -1 || twoOrMoreChildren ? '.' : ''}</>);
        }
        const pensionOnly = gfPensionOnlyLeaderLine({ ...row, recommendationLabels: labelsFor(row) }, movers, base, { hostReceived: given, legacyYears: horizonYears, factFind, money });
        // Beside the figures with every "why it is shown" (EnginePlanCard whyShown; 23 Oct 2026).
        const pensionOnlyWhy = pensionOnly && m.home !== 'transferred' ? pensionOnly : null;
        // "Worth knowing:" with a colon on every line, as aged care's card lines (owner, 22 Oct 2026: one voice across modules).
        // A decided form is the only form the plans use (25 Oct 2026); this line now speaks only for plans tested
        // before step 1 changed (an earlier run on screen), so they are never read as what was described.
        const otherForm = gfOtherFormLine(gfGiveOf(factFind), m);
        if (otherForm) lines.unshift(<span data-testid="gf-other-form"><strong className="text-slate-900">Not what was described:</strong> {otherForm}</span>);
        if (m.home === 'rented') lines.push(<><strong className="text-slate-900">Worth knowing:</strong> {RENTED_HOME_CGT_CARD}</>);
        if (m.home === 'kept') lines.push(<><strong className="text-slate-900">Worth knowing:</strong> the empty home earns nothing, but Centrelink counts it from the day {v.they} move.</>);
        if (m.home === 'sold') lines.push(<><strong className="text-slate-900">Worth knowing:</strong> the money from the sale is counted for the Age Pension wherever it is put.</>);
        if (m.justMovingIn && m.home) lines.push(<><strong className="text-slate-900">Worth knowing:</strong> paying nothing does not protect the Age Pension. The home {v.they} leave is counted from the day {v.they} move.</>);
        if (gfGiftCounted(m.deprivation) > 0) lines.push(<><strong className="text-slate-900">Worth knowing:</strong> {money(m.deprivation)} is paid above what the right can be valued at; less the {money(GIFTING_LIMIT_ANNUAL)} that can be given freely, {money(gfGiftCounted(m.deprivation))} counts as a gift. It is still counted as {v.you ? 'yours' : 'theirs'} for five years though it has gone.</>);
        if ((row.fundingShortfall ?? 0) > 0) lines.push(<><strong className="text-rose-700">Important:</strong> {money(row.fundingShortfall ?? 0)} of the payment cannot be found from {v.their} savings, so this is not yet a plan that can go ahead.</>);
        const gi = gfGuaranteedIncomeOf(row, factFind);
        if (gi?.incomeOnly) lines.push(<><strong className="text-slate-900">Worth knowing:</strong> the guaranteed income is income only. It pays the most for life, and nothing from it goes back to the family, even after an early death.</>);
        const isChosen = chosenId != null && row.id === chosenId;
        const compare = gi ? gfComparePlanFor(row, strategies) : null;
        const familyPanel = gi ? (
            <AnnuityFamilyPanel
                factFind={factFind}
                personName={gi.personName}
                amount={gi.amount}
                kind={gi.kind}
                incomeAYear={gi.incomeAYear}
                rows={row.projections as never}
                compareRows={(compare?.projections as never) ?? null}
                compareNote={compare ? GF_COMPARE_NOTE : null}
                horizonYears={horizonYears}
                testId={`gf-family-panel-${row.id}`}
            />
        ) : null;
        const tradeOff = secondEntry?.second && row === secondRow ? secondPlanLine(secondEntry.second, formatCurrency) : estateEntry?.lead && row.name === estateEntry.strategy?.name ? leadLine(estateEntry.lead, formatCurrency) : null;
        const held = !!gi && gi.incomeOnly && incomeOnlyHeld(factFind, true, gi.amount);
        return (
            <EnginePlanCard
                key={key}
                aligned
                testId="gf-plan-card"
                whyShown={tradeOff || pensionOnlyWhy || null}
                chip={chip}
                chipTone="blue"
                title={words.title}
                sub={words.sub}
                legacy={legacyOf(row)}
                legacyDelta={delta}
                legacyCaption={<>in {estate} after about {yrs}</>}
                lessLabel="Less in the estate"
                horizonYears={horizonYears}
                baselineWord={benchmarkWords('grannyFlat', { rents: householdRents(factFind as never) }).word}
                bars={delta != null ? { gainPct: (Math.abs(delta) / maxGain) * 100, tiedUp: given, tiedUpPct: (given / maxGiven) * 100 } : null}
                tiedUpLabel={`Given to ${host}`}
                hideEmptyTiedUp
                numbers={numbers}
                runline={runline}
                familyPanel={familyPanel}
                worthKnowing={lines.length ? <>{lines.map((l, i) => <p key={i}>{l}</p>)}</> : null}
                lines={[]}
                chosen={isChosen}
                beforeChoose={gi?.incomeOnly && onIncomeOnly ? <div data-testid={`gf-choose-held-${row.id}`} data-held={held ? 'true' : 'false'}><IncomeOnlyBeforeChoose factFind={factFind} amount={gi.amount} onChange={onIncomeOnly} /></div> : undefined}
                chooseHeld={held}
                onExplain={() => onExplain(row)}
                onRisks={() => onRisks(row)}
                onChoose={() => onChoose(row)}
                chooseLabel={chosenId != null && !isChosen ? 'Choose this instead' : 'Choose this plan'}
                chosenStrip={<ChosenAdviceLine possessiveFile={v.possessive} onAdvice={onAdvice} onCustomise={() => onBuildYourOwn(row)} extra={<button type="button" onClick={onFamilyGuide} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">The family guide</button>} />}
                detail={renderDetail(row)}
                detailOpen={openDetail === key}
                onToggleDetail={() => setOpenDetail((d) => (d === key ? null : key))}
                detailHint="where the money comes from and every calculation"
            />
        );
    };

    const Strip = () => (
        <div data-testid="gf-stay-put-strip">
            <p className="text-[13px] font-semibold text-slate-500">Compared with staying put, not entering the arrangement — the four numbers from step 2</p>
            <div className="mt-2 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 shadow-card lg:grid-cols-4">
                {[
                    { label: 'Age Pension', value: money(pensionOf(base)), note: 'a year' },
                    { label: cashOf(base) < 0 ? 'Short each year' : 'Left over each year', value: money(Math.abs(cashOf(base))), note: 'money in, less money out', tone: cashOf(base) >= 0 ? 'text-emerald-700' : 'text-rose-700' },
                    { label: `What ${v.they} own`, value: money(ownToday), note: 'today' },
                    { label: 'Left to the family', value: money(legacyOf(base)), note: `after about ${yrs}` },
                ].map((c) => (
                    <div key={c.label} className="bg-white px-4 py-3">
                        <p className="text-[12px] font-semibold text-slate-500">{c.label}</p>
                        <p className={`mt-0.5 text-[22px] font-bold tabular-nums leading-tight ${c.tone || 'text-slate-900'}`}>{c.value}</p>
                        <p className="text-[12px] text-slate-500">{c.note}</p>
                    </div>
                ))}
            </div>
        </div>
    );

    const goalSubs: Partial<Record<GoalFocusId, string>> = {
        legacy: verdict?.cost ? 'Smallest cost to the estate' : `Biggest estate after about ${yrs}`,
        covered: coveredSame ? 'Same plan as "leave the most"' : 'Pays its way without savings',
        cashflow: 'Easiest cashflow',
        pension: noPensionAnyWay ? 'No way of setting it up keeps any' : keepers.length < movers.length ? 'Keeps it, where others lose it' : 'Most government support',
    };

    return (
        <div className="space-y-6" data-testid="gf-plans">
            {gfBuildNeedsCost(factFind) && (
                <p className="rounded-xl border border-slate-200 border-l-4 border-l-rose-500 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="gf-plans-build-needs-cost">
                    <strong className="text-slate-900">No build plan is tested until the cost is entered.</strong> Enter the builder&apos;s quote on step 1 to see plans that pay for the build.
                </p>
            )}
            {notices}
            {base && <Strip />}
            {verdict && (
                <div className={`rounded-xl border border-slate-200 border-l-4 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card ${verdict.cost ? 'border-l-rose-500' : 'border-l-emerald-500'}`} data-testid="gf-verdict">
                    {(() => {
                        const first = verdict.text.indexOf('. ');
                        return first > 0 ? <><strong className="text-slate-900">{verdict.text.slice(0, first + 1)}</strong>{verdict.text.slice(first + 1)}</> : verdict.text;
                    })()}
                </div>
            )}

            <EngineGoalControl
                value={focus}
                onChange={onFocus}
                household={v.names}
                enteringName={v.names}
                horizonYears={horizonYears}
                coveredSameAsBest={coveredSame}
                bestCovers={coveredSame && cashOf(fullRow(goalLeaderFor(recommended, 'legacy'))) >= 0}
                waysTested={movers.length}
                shown={shown.length}
                testedFor="to set it up"
                closingNote={<>Each card says what {v.they} give {host}, what counts as a gift, and what is left in {estate}.</>}
                subs={goalSubs}
                countLine={showPensionNote ? 'No way of setting it up keeps any Age Pension.' : shown.length === 0 ? 'None leads on this goal.' : undefined}
            />

            {showPensionNote ? (
                <div className="rounded-2xl border border-slate-200 border-l-4 border-l-brand-navy bg-white p-6 shadow-card" data-testid="gf-no-pension">
                    <h3 className="text-[19px] font-bold text-slate-900">No way of setting it up keeps {v.possessive} Age Pension</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-700">
                        {pensionOf(base) >= 1
                            ? <>Staying put, {v.they} get <strong>{money(pensionOf(base))} a year</strong>. Once {v.they} move, the home stops being exempt: kept, it counts in full; sold, the money counts wherever it is put. Either way Centrelink counts more than {v.couple ? 'a couple is' : 'one person is'} allowed.</>
                            : <>Staying put, Centrelink already counts more than {v.couple ? 'a couple is' : 'one person is'} allowed, and moving in doesn&apos;t change that.</>}
                    </p>
                    <p className="mt-3 text-[15px] leading-relaxed text-slate-700">So this goal can&apos;t tell the plans apart. Choose one of the other three goals to compare them.</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        <button type="button" onClick={() => onFocus('legacy')} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">Compare the plans on another goal</button>
                    </div>
                </div>
            ) : shown.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="gf-no-leader">
                    <h3 className="text-[19px] font-bold text-slate-900">No plan leads on this goal</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-700">Choose another goal above, or see all {movers.length} ways tested.</p>
                </div>
            ) : (
                <div className={`grid grid-cols-1 gap-5 ${shown.length > 1 ? 'lg:grid-cols-2' : ''}`} data-testid="gf-plan-cards">
                    {chosenId != null && !shown.some((s) => s.id === chosenId) && (
                        <div className={shown.length > 1 ? 'lg:col-span-2' : ''}><ChosenAdviceLine possessiveFile={v.possessive} onAdvice={onAdvice} offCard testId="chosen-plan-off-card" /></div>
                    )}
                    {shown.map(card)}
                </div>
            )}
            {shown.length > 0 && <OtherGoalLeaders others={others} here={{ legacy: legacyOf(shown[0]), cashflow: cashOf(shown[0]), pension: pensionOf(shown[0]) }} baseLegacy={base ? legacyOf(base) : null} baseWord="staying put" onFocus={onFocus} testId="gf-other-leaders" />}

            {/* "See it anyway" opens the plan's own card beneath the note (owner, 23 Oct 2026; SetAsideReveal). */}
            {setAside.map((sa) => (
                <SetAsideReveal
                    key={String(sa.strategy.id ?? sa.strategy.name)}
                    testId="gf-set-aside"
                    className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700"
                    note={(link) => <p>{setAside.length === 1 ? 'One plan was set aside: ' : ''}{lower(gfPlanWords(sa.strategy, factFind).title)} would lead on {sa.labels.map((l) => ({ 'Optimal Legacy': 'the estate', 'Optimal Cashflow': 'cashflow', 'Optimal Age Pension': 'the Age Pension' }[l] || 'one goal')).join(' and ')}, but {lower(sa.warning)}{' '}{link}</p>}
                    renderCard={() => card(sa.strategy)}
                />
            ))}

            <div className="flex flex-wrap items-center gap-3" data-testid="gf-ways-on">
                {shown.length > 0 && <button type="button" onClick={() => setComparing(true)} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="gf-compare">Compare the plans side by side</button>}
                <button type="button" onClick={onAllWays} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="gf-all-ways">All {movers.length} ways tested</button>
                <button type="button" onClick={onFamilyGuide} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="gf-plans-guide">The family discussion guide</button>
                <button type="button" onClick={onChanges} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="gf-changes">Things that change the answer</button>
                <button type="button" onClick={() => onBuildYourOwn(null)} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="gf-build-your-own">Build a plan yourself →</button>
            </div>
            <p className="text-[13px] text-slate-500" data-testid="all-plans-open-line">{allPlansOpenLine(movers.length)}</p>
            <p className="text-[13px] text-slate-500">Figures use the rates in force today and are projections over {yrs}, not guarantees. Always get legal and financial advice before committing.</p>

            {comparing && (
                <DialogShell titleId="gf-compare-title" title="The plans side by side" eyebrow={`Over about ${yrs}`} onClose={() => setComparing(false)} size="xl" testId="gf-compare-dialog" flush>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-[14px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500">
                                    <th className="px-5 py-3"> </th>
                                    {base && <th className="px-5 py-3">Staying put</th>}
                                    {shown.map((s) => <th key={String(s.id)} className="px-5 py-3">{gfPlanWords(s, factFind).title}</th>)}
                                </tr>
                            </thead>
                            <tbody className="tabular-nums">
                                {[
                                    { label: `In ${estate}`, pick: legacyOf, baseCell: (b: GfStrategyView) => money(legacyOf(b)) },
                                    { label: `Given to ${host}`, pick: (s: GfStrategyView) => gfGivenOf(gfMoveOf(s, factFind)), baseCell: () => '—' },
                                    { label: 'Age Pension, a year', pick: pensionOf, baseCell: (b: GfStrategyView) => money(pensionOf(b)) },
                                    { label: 'Left over each year', pick: cashOf, baseCell: (b: GfStrategyView) => money(cashOf(b)) },
                                    { label: 'Counted as a gift', pick: (s: GfStrategyView) => gfGiftCounted(gfMoveOf(s, factFind).deprivation), baseCell: () => '—' },
                                ].map((r) => (
                                    <tr key={r.label} className="border-b border-slate-100">
                                        <th className="px-5 py-3 text-left font-semibold text-slate-700">{r.label}</th>
                                        {base && <td className="px-5 py-3">{r.baseCell(base)}</td>}
                                        {shown.map((s) => <td key={String(s.id)} className="px-5 py-3 font-semibold text-slate-900">{money(r.pick(s))}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </DialogShell>
            )}

            {why && (
                <DialogShell
                    titleId="gf-why-title"
                    eyebrow={gfPlanWords(why.row, factFind).title}
                    title={why.page === 'pension' ? 'Why the Age Pension is what it is' : why.page === 'cashflow' ? 'Left over each year' : 'Counted as a gift'}
                    onClose={() => setWhy(null)}
                    testId="gf-why-dialog"
                >
                    {why.page === 'pension' && (
                        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                            <p>Staying put: <strong>{money(pensionOf(base))} a year</strong>. With this plan: <strong>{money(pensionOf(why.row))} a year</strong> in the first year.{gfPensionReason(why.row, factFind) ? <> The difference is mostly because {gfPensionReason(why.row, factFind)}.</> : null}</p>
                            {gfMoveOf(why.row, factFind).entryContribution > 0 && (gfMoveOf(why.row, factFind).entryContribution > EXTRA_ALLOWABLE_AMOUNT
                                ? <p>{v.They} give more than {money(EXTRA_ALLOWABLE_AMOUNT)} for the right, so {v.they} stay homeowners and the right itself is not counted.</p>
                                : <p>{v.They} give {money(gfMoveOf(why.row, factFind).entryContribution)}, not more than {money(EXTRA_ALLOWABLE_AMOUNT)}, so {v.they} are not homeowners for the Age Pension: the right counts as an asset, and the higher non-homeowner limits apply.</p>)}
                            {(() => {
                                const f = why.row.projections?.[0]?.financials as FinancialPosition | undefined;
                                const pos = readMeansTestPosition((f as { socialSecurityBreakdown?: MeansTestBreakdownRow[] } | undefined)?.socialSecurityBreakdown, (f as { socialSecurity?: number } | undefined)?.socialSecurity, factFind?.adviceFor === 'couple');
                                if (!pos) return null;
                                return (
                                    <p data-testid="gf-why-pension-count">
                                        With this plan Centrelink counts <strong>{money(pos.assessableAssets)}</strong> of assets and <strong>{money(pos.assessableIncome)} a year</strong> of income.
                                        {' '}The {pos.determinedBy === 'income' ? 'income' : 'assets'} test sets the pension: it gives {money(pos.determinedBy === 'income' ? pos.incomeTestPension : pos.assetTestPension)} a year, against {money(pos.determinedBy === 'income' ? pos.assetTestPension : pos.incomeTestPension)} on the other test, and the lower of the two is paid.
                                    </p>
                                );
                            })()}
                        </div>
                    )}
                    {why.page === 'cashflow' && (
                        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                            <p>Money in, less money out, in the first year: <strong>{cashOf(why.row) < 0 ? `short ${money(-cashOf(why.row))}` : `${money(cashOf(why.row))} to spare`}</strong>. Not entering: {cashOf(base) < 0 ? `short ${money(-cashOf(base))}` : `${money(cashOf(base))} to spare`}.</p>
                            <p>{(() => {
                                const m = gfMoveOf(why.row, factFind);
                                if (m.home === 'rented') return 'The rent from the home, after its costs and the weeks it sits empty, is part of the money in; the lost Age Pension is part of the difference.';
                                if (m.home === 'sold') return 'What the sale money earns, wherever it is put, is part of the money in.';
                                return 'What is paid for the right no longer earns anything, and any change in the Age Pension is part of the difference.';
                            })()}</p>
                            <p>Explain this sets out every line of it, year by year.</p>
                        </div>
                    )}
                    {why.page === 'gift' && (() => {
                        const m = gfMoveOf(why.row, factFind);
                        return (
                            <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                                <p>{v.They} give <strong>{money(m.entryContribution)}</strong> for the right to live with {host}.</p>
                                {m.amountPaid === 0 && (m.propertyTransferred > 0 || m.construction > 0)
                                    ? <p>{m.propertyTransferred > 0 ? 'The home’s title' : 'The cost of the build'}, on its own, is taken at face value as the price of the right, so none of it is a gift, whatever it is worth.</p>
                                    : m.deprivation > 0
                                        ? <p>Cash is tested: the right can be valued up to Centrelink&apos;s limit, or the home or build it comes with if that is more. <strong>{money(m.deprivation)}</strong> is above that. Less the {money(GIFTING_LIMIT_ANNUAL)} a year that can be given freely, <strong>{money(gfGiftCounted(m.deprivation))}</strong> counts as a gift: Centrelink keeps counting it as {v.you ? 'yours' : 'theirs'}, and deems it to earn income, for five years, then it drops off.</p>
                                        : <p>Cash is tested against Centrelink&apos;s value of the right. This plan pays no more than that value, so none of it is a gift.</p>}
                            </div>
                        );
                    })()}
                </DialogShell>
            )}
        </div>
    );
};

export default GfPlansView;
